import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DiscordCategoryPicker } from '@/components/discord/DiscordPickers';
import { ApiError } from '@/lib/api';
import {
  useCreateTicketCategory,
  useDeleteTicketCategory,
  useTicketCategories,
} from '@/lib/queries';

export function CategoriesPage() {
  const cats = useTicketCategories();
  const create = useCreateTicketCategory();
  const remove = useDeleteTicketCategory();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [targetCategoryId, setTargetCategoryId] = useState('');
  const [description, setDescription] = useState('');

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync({
        name,
        emoji: emoji || undefined,
        description: description || undefined,
        targetDiscordCategoryId: targetCategoryId || undefined,
      });
      toast.success('Category created');
      setOpen(false);
      setName('');
      setEmoji('');
      setTargetCategoryId('');
      setDescription('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleDelete(id: string, name: string) {
    if (
      !confirm(
        `Delete category "${name}"?\n\n` +
          `All tickets in this category — including their messages and transcripts — will be ` +
          `permanently removed from the database. Discord channels are not touched. This cannot be undone.`,
      )
    )
      return;
    try {
      await remove.mutateAsync(id);
      toast.success('Deleted');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ticket categories</h1>
          <p className="text-muted-foreground">Each category becomes a button on a ticket panel</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>New category</DialogTitle>
                <DialogDescription>
                  You can configure the welcome embed and auto-close policy after creation.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-[1fr_auto] gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Support"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emoji">Emoji</Label>
                    <Input
                      id="emoji"
                      value={emoji}
                      onChange={(e) => setEmoji(e.target.value)}
                      placeholder="🎫"
                      maxLength={4}
                      className="w-20"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Discord category (where ticket channels are created)</Label>
                  <DiscordCategoryPicker
                    value={targetCategoryId}
                    onValueChange={setTargetCategoryId}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (admin-only, optional)</Label>
                  <Input
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="General questions"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={create.isPending}>
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Categories</CardTitle>
          <CardDescription>{cats.data?.length ?? 0} total</CardDescription>
        </CardHeader>
        <CardContent>
          {cats.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">Create your first category.</p>
          )}
          {cats.data && cats.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead>Discord category ID</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {cats.data.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <Link
                        to={`/tickets/categories/${c.id}`}
                        className="flex items-center gap-2 hover:underline"
                      >
                        {c.emoji && <span>{c.emoji}</span>}
                        <span className="font-medium">{c.name}</span>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={c.enabled ? 'default' : 'secondary'}>
                        {c.enabled ? 'on' : 'off'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {c.targetDiscordCategoryId ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(c.id, c.name)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
